import { Link } from 'react-router-dom';
import { CATEGORIES } from '../../data/catalog';
import { featuredInteriorRoutes, productPhotos } from '../../data/homeImages';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import { ArrowRight, Check } from '../ui/Icons';

const category = CATEGORIES.interior;

/**
 * Featured interior products come straight from the catalogue so the titles
 * and links always resolve to a real product page. The lifestyle photo for
 * each card lives in homeImages; anything without one falls back to the
 * pack shot.
 */
const FEATURED = featuredInteriorRoutes
  .map((route) => category.products.find((p) => p.route === route))
  .filter((p): p is NonNullable<typeof p> => Boolean(p))
  .concat(category.products.filter((p) => !featuredInteriorRoutes.includes(p.route)))
  .slice(0, 3);

const HIGHLIGHTS = ['Scrub-resistant, wipe-clean surface', 'Low odour — rooms usable sooner', 'Even coverage with fewer coats'];

const InteriorPaints = () => (
  <section className="vp-section bg-brand-surface">
    <div className="container">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        {/* Intro */}
        <div className="lg:col-span-4 lg:sticky lg:top-28">
          <SectionHeading eyebrow={category.eyebrow} title={category.tagline} lead={category.description} size="md" />

          <ul className="mt-6 space-y-3">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-start gap-3 text-sm text-brand-ink/85">
                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                  <Check size={12} />
                </span>
                {h}
              </li>
            ))}
          </ul>

          <Link to={category.route} className="vp-btn vp-btn-primary mt-8">
            View all interior paints
            <ArrowRight size={16} />
          </Link>
        </div>

        {/* Cards */}
        <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-5 lg:gap-6">
          {FEATURED.map((p, i) => {
            const photo = productPhotos[p.route];
            const title = p.tagline && !p.title.includes(p.tagline) ? `${p.title} ${p.tagline}` : p.title;
            return (
              <Reveal as="li" key={p.route} delay={Math.min(i, 4) as 0 | 1 | 2 | 3 | 4} className={i === 0 ? 'sm:col-span-2' : ''}>
                <Link to={p.route} className="vp-card group flex flex-col h-full">
                  <div className={`relative overflow-hidden bg-brand-surface ${i === 0 ? 'h-56 sm:h-72' : 'h-48'}`}>
                    {photo ? (
                      <img src={photo.image} alt={photo.alt} className="vp-zoom w-full h-full object-cover" loading="lazy" />
                    ) : (
                      p.image && <img src={p.image} alt={`${title} pack`} className="vp-zoom w-full h-full object-contain p-8" loading="lazy" />
                    )}
                    {photo?.tag && <span className="absolute top-3 left-3 vp-tag">{photo.tag}</span>}
                    {p.warranty && <span className="absolute top-3 right-3 vp-tag !bg-brand-gold !text-brand-ink">{p.warranty}</span>}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-lg font-semibold text-brand-ink group-hover:text-brand-blue transition-colors">{title}</h3>
                    {p.features.length > 0 && (
                      <ul className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5 flex-1">
                        {p.features.slice(0, 3).map((f) => (
                          <li key={f} className="flex items-center gap-1.5 text-[13px] text-brand-muted">
                            <Check size={11} className="text-brand-red flex-shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    )}
                    <span className="mt-4 vp-link text-sm">
                      Product details
                      <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </ul>
      </div>
    </div>
  </section>
);

export default InteriorPaints;
